/**
 * §rbac (2026-05-08) — terminal page for operators whose role grants
 * no view permissions on any sidebar destination. Reached by:
 *   * Login.tsx when ``firstAccessibleRoute`` returns NO_ACCESS_ROUTE
 *   * permissions.tsx ``Guard`` when the redirect target itself
 *     would also fail the permission check
 *
 * Renders without an AppShell (the shell would also have nothing to
 * show) and offers logout so the operator isn't stuck in an empty
 * tab.
 */

import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { ShieldAlert } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { logout } from "@/lib/api";
import { clearSession, getOperator, getTenantId } from "@/lib/auth";
import {
  firstAccessibleRoute,
  NO_ACCESS_ROUTE,
} from "@/lib/firstAccessibleRoute";
import { getMe } from "@/lib/rbac";

export function NoAccessPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const operator = getOperator();
  const tenant = getTenantId();

  // §stale-session (2026-08-10) — belt-and-braces: this page is the terminal
  // stop for "role grants zero .view permissions", but dead sessions used to
  // be MISROUTED here (an errored /me read as empty perms). Re-validate once
  // with a FRESH /me — not the react-query cache, which may hold the very
  // error that misrouted us:
  //   * fetch fails (401 / dead refresh / network) → the session is dead;
  //     sign out automatically instead of making the operator click Sign out
  //   * fetch succeeds WITH view perms → we never belonged here; leave for
  //     the first accessible route
  //   * fetch succeeds with zero perms → genuinely restricted; stay put.
  const revalidatedRef = useRef(false);
  useEffect(() => {
    if (revalidatedRef.current) return;
    revalidatedRef.current = true;
    void (async () => {
      try {
        const me = await getMe();
        const dest = firstAccessibleRoute(me.permissions);
        if (dest !== NO_ACCESS_ROUTE) navigate(dest, { replace: true });
      } catch {
        clearSession();
        queryClient.clear();
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, queryClient]);

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      clearSession();
      queryClient.clear();
      navigate("/login", { replace: true });
    }
  };

  return (
    <div
      role="alert"
      className="flex min-h-screen items-center justify-center px-6"
      style={{ background: "var(--canvas-base, #0c0e18)" }}
    >
      <div
        className="w-full max-w-md rounded-2xl border p-8 text-center"
        style={{
          background: "var(--glass-1)",
          borderColor: "var(--glass-hairline-strong)",
          boxShadow: "var(--shadow-rim), 0 30px 80px -20px rgba(0,0,0,0.6)",
          backdropFilter: "blur(28px) saturate(180%)",
        }}
      >
        <div
          className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full"
          style={{
            background: "color-mix(in oklab, var(--warning) 18%, transparent)",
            border: "1px solid color-mix(in oklab, var(--warning) 40%, transparent)",
            color: "var(--warning)",
          }}
        >
          <ShieldAlert className="h-6 w-6" aria-hidden />
        </div>
        <h1
          className="text-xl font-semibold"
          style={{
            fontFamily: "var(--font-display)",
            color: "var(--fg-1)",
            letterSpacing: "-0.015em",
          }}
        >
          No pages available
        </h1>
        <p
          className="mt-2 text-sm"
          style={{ color: "var(--fg-3)", lineHeight: 1.55 }}
        >
          {operator?.display_name || operator?.username || "This account"}'s
          role doesn't grant view access to any page in this workspace. Ask
          your administrator to assign at least one <code>.view</code>{" "}
          permission, then sign back in.
        </p>
        <div
          className="mt-4 inline-flex items-center gap-2 rounded-full border px-3 py-1"
          style={{
            background: "var(--glass-2)",
            borderColor: "var(--glass-hairline)",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            color: "var(--fg-3)",
            letterSpacing: "0.04em",
          }}
        >
          <span>tenant · {tenant}</span>
          {operator?.role_name ? (
            <>
              <span>·</span>
              <span>role · {operator.role_name.toLowerCase()}</span>
            </>
          ) : null}
        </div>
        <div className="mt-6">
          <Button variant="primary" onClick={handleLogout} className="w-full">
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}
